import { cn, Divider, Inline, Stack } from '@reelplicate/ui';
import type { ReactNode } from 'react';

import { StatusIcon, type StatusIconProps } from './status-icon';

/**
 * Figma auth "Card": bg/canvas, 1px border/subtle, radius/2xl, 32px top and
 * sides, 28px bottom, 20px between Header, Body, Separator and Footer.
 */
export function AuthCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <Stack
      gap={5}
      className={cn(
        'bg-bg-canvas border-border-subtle w-full rounded-2xl border px-8 pt-8 pb-7',
        className,
      )}
    >
      {children}
    </Stack>
  );
}

type AuthHeaderProps = {
  title: string;
  subtitle: ReactNode;
  /** Figma "Auth / Status icon" above the title, on status screens. */
  icon?: StatusIconProps;
};

/** Card "Header": optional status icon, then the H2 title and subtitle, centred. */
export function AuthHeader({ title, subtitle, icon }: AuthHeaderProps) {
  return (
    <Stack gap={4} align="center" className="pb-1 text-center">
      {icon && <StatusIcon {...icon} />}
      <Stack gap={2} align="center">
        <h1 className="text-heading-h2">{title}</h1>
        <p className="text-body-md text-text-secondary">{subtitle}</p>
      </Stack>
    </Stack>
  );
}

/** Card "Body": the 16px-gap stack of fields and actions. */
export function AuthBody({ children }: { children: ReactNode }) {
  return <Stack gap={4}>{children}</Stack>;
}

/** Card "Separator" + "Footer": the centred "Question? Link" row. */
export function AuthFooter({ children }: { children: ReactNode }) {
  return (
    <>
      <Divider tone="subtle" />
      <Inline gap={1} align="center" className="text-body-md text-text-secondary">
        {children}
      </Inline>
    </>
  );
}
